import { ArrowUpward, QuestionAnswer } from '@mui/icons-material';
import { alpha, Avatar, Box, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setSelectedItem, setSortSelectedItem } from 'app/actions/issue';
import { Autosizer, Scrollbar } from 'app/components';
import { selectedItemIssueSelector, sortItemsIssueSelector, sortSelectedItemIssueSelector } from 'app/selectors/issue';
import React, { createContext, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const IssueListContext = createContext();

const IssueList = function () {
  const dispatch = useDispatch();
  const selectedItem = useSelector(selectedItemIssueSelector);
  const sortSelectedItem = useSelector(sortSelectedItemIssueSelector);
  const sortItems = useSelector(sortItemsIssueSelector);

  const handleItemClick = (value) => dispatch(setSelectedItem(value));

  const handleSortClick = (value) => dispatch(setSortSelectedItem(value));

  return (
    <Box
      component='div'
      sx={{
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-horizontal': {
          right: '10px',
        },
        '.simplebar-vertical': {
          top: '30px',
          bottom: '0',
        },
      }}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <IssueListContext.Provider
              value={{
                onSortClick: handleSortClick,
                sortSelectedItem,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    itemSize={24}
                    itemCount={sortItems.length}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    innerElementType={IssueListInnerElementType}
                    itemData={{
                      items: sortItems,
                      onClick: handleItemClick,
                      selectedItem,
                    }}>
                    {IssueListItemRender}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </IssueListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const IssueListInnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <IssueListContext.Consumer>
    {({ onSortClick, sortSelectedItem }) => (
      <List
        ref={ref}
        sx={{
          height: style.height,
          minHeight: '100%',
        }}
        {...rest}>
        <ListItem
          disablePadding
          sx={(theme) => ({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            minHeight: '100%',
            zIndex: '2',
            pointerEvents: 'none',
            padding: theme.spacing(0),
            '.MuiListItemText-root': {
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
              position: 'relative',
              flex: 'none',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                height: '100%',
                bgcolor: theme.palette.divider,
                width: '1px',
                zIndex: '1',
                pointerEvents: 'none',
              },
              '&:last-of-type': {
                flex: '1',
                '&:before': {
                  display: 'none',
                },
              },
            },
          })}>
          <ListItemText style={{ width: '44px' }} />
          {config.issueColumns.map((column) => (
            <ListItemText key={column.name} style={{ width: column.width }} />
          ))}
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            position: 'sticky !important',
            top: '0',
            zIndex: '1',
            '.MuiListItemButton-root': {
              display: 'flex',
              flex: 'none',
              padding: theme.spacing(0),
              position: 'relative',
              bgcolor: 'background.default',
              borderColor: theme.palette.divider,
              '&:first-of-type': {
                '.MuiListItemText-root': {
                  paddingLeft: '31px',
                },
              },
              '&:last-of-type': {
                flex: '1',
                '.MuiListItemText-root': {
                  paddingRight: theme.spacing(2),
                },
              },
              '.MuiTouchRipple-rippleVisible': {
                color: 'inherit',
              },
              '.MuiListItemText-root': {
                display: 'flex',
                cursor: 'pointer',
                margin: theme.spacing(0),
                padding: '6px 6px',
                alignItems: 'center',
                justifyContent: 'space-between',
                '.MuiTypography-root': {
                  display: 'flex',
                  '&:nth-of-type(1)': {
                    color: 'text.secondary',
                    marginRight: '4px',
                  },
                  '&:nth-of-type(2) svg': {
                    fontSize: '12px',
                    transition: 'all 300ms',
                    transform: 'rotateZ(0deg)',
                  },
                },
              },
            },
          })}>
          <ListItemButton disableTouchRipple style={{ minWidth: '44px', minHeight: '30px' }}>
            <ListItemText disableTypography />
          </ListItemButton>
          {config.issueColumns.map((column) => (
            <ListItemButton
              key={column.name}
              onClick={() => onSortClick(column)}
              disableTouchRipple={!Object.prototype.hasOwnProperty.call(column, 'order')}
              sx={(theme) => ({
                minWidth: column.width,
                '.MuiListItemText-root': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  '.MuiTypography-root': {
                    width: 'auto',
                    '&:nth-of-type(2)': {
                      opacity: '0.2',
                      transform: 'rotateZ(0)',
                      transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                      ...(sortSelectedItem.name === column.name && {
                        opacity: '1',
                        ...(sortSelectedItem.order === 'DESC' && {
                          transform: 'rotateZ(180deg)',
                        }),
                      }),
                    },
                  },
                },
              })}>
              <ListItemText disableTypography>
                <Typography>{column.label}</Typography>
                <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
              </ListItemText>
            </ListItemButton>
          ))}
        </ListItem>
        {children}
      </List>
    )}
  </IssueListContext.Consumer>
));

const IssueListItemRender = ({ data, index, style }) => {
  const { items, selectedItem, onClick } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      style={style}
      selected={items[index].id === selectedItem?.id}
      onClick={() => onClick(items[index])}
      sx={(theme) => ({
        width: '100%',
        top: `${style.top + 30}px!important`,
        userSelect: 'none',
        borderRadius: '0',
        '.MuiListItemAvatar-root': {
          minWidth: '20px',
        },
        '.MuiAvatar-circular': {
          width: '20px',
          height: '20px',
          bgcolor: alpha(theme.palette.grey.main, 0.12),
          svg: {
            fontSize: '14px',
          },
        },
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiAvatar-circular': {
            bgcolor: alpha(theme.palette.primary.contrastText, 1),
          },
        },
        '.MuiListItemButton-root': {
          '&:first-of-type': {
            paddingLeft: '7px',
            paddingTop: '3px',
            paddingBottom: '3px',
          },
          '&:hover': {
            bgcolor: 'inherit',
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            svg: {
              fontSize: '12px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      <ListItemText style={{ width: '44px' }}>
        <ListItemAvatar>
          <Avatar>
            <QuestionAnswer fontSize='small' sx={{ color: items[index].status_color }} />
          </Avatar>
        </ListItemAvatar>
      </ListItemText>
      {config.issueColumns.map((column) => (
        <ListItemText key={column.name} style={{ width: column.width }}>
          <Typography>{column.format ? column.format(items[index][column.name]) : items[index][column.name]}</Typography>
        </ListItemText>
      ))}
    </ListItem>
  );
};

export default IssueList;
